import { create } from "zustand";
import { HttpClient } from "../services/api/httpclient.service";

export type MasteryTier = "mastered" | "good" | "partial" | "struggling" | "unattempted";

export interface MatrixTopicHeader {
  topic_id: number;
  topic_name: string;
  topic_code: number;
}

export interface MatrixTopicFactorDetails {
  streak_days: number;
  streak_factor: number;
  lesson_progress: number;
  has_lessons: boolean;
  quiz_accuracy: number;
  has_quizzes: boolean;
  quiz_correct_count?: number;
  curric_quiz_target?: number;
  distinct_exercises: number;
  target_exercises: number;
  exercise_breadth_factor: number;
  pass_count: number;
  avg_pass_score: number;
  partial_count: number;
  avg_partial_score: number;
}

export interface MatrixStudentTopicCell {
  topic_id: number;
  mastery_percent: number;
  tier: MasteryTier;
  has_activity: boolean;
  lesson_progress_percent: number;
  practice_score_percent: number;
  distinct_exercises_count: number;
  submissions_count: number;
  last_activity_at?: string | null;
  factors?: MatrixTopicFactorDetails;
}

export interface MatrixStudentRow {
  student_id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  avatar_initials: string;
  rank_key?: string;
  rank_label?: string;
  rank_icon?: string;
  points?: number;
  streak?: number;
  progress?: number;
  score?: number;
  status_key?: string;
  percentile?: number;
  cells: MatrixStudentTopicCell[];
}


export interface MatrixClassAverageCell {
  topic_id: number;
  average_mastery_percent: number;
  tier: MasteryTier;
  active_students_count: number;
  total_students_count: number;
}

export interface MatrixClassCohort {
  class_id: number;
  class_name: string;
  units?: number | null;
  student_count: number;
  topics: MatrixTopicHeader[];
  students: MatrixStudentRow[];
  class_averages: MatrixClassAverageCell[];
}

export interface TeacherTopicMasteryMatrixResponse {
  teacher_id: number;
  selected_class_id?: number | null;
  classes: MatrixClassCohort[];
}

export interface TeacherClassItem {
  id: number | null;
  name: string;
  units: number | null;
  student_count: number;
  icon: string;
}

export interface AdminTeacherItem {
  id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  email: string;
  class_count: number;
  student_count: number;
}

interface TeacherStoreState {
  classes: TeacherClassItem[];
  selectedClassId: number | null;
  setClasses: (classes: TeacherClassItem[]) => void;
  setSelectedClass: (id: number | null) => void;

  selectedStudentId: number | null;
  setSelectedStudentId: (id: number | null) => void;

  adminTeachers: AdminTeacherItem[];
  adminSelectedTeacherId: number | null;
  isLoadingAdminTeachers: boolean;
  setAdminTeachers: (teachers: AdminTeacherItem[]) => void;
  setAdminSelectedTeacherId: (id: number | null) => void;
  setIsLoadingAdminTeachers: (isLoading: boolean) => void;

  // Matrix State (KAN-339)
  matrixData: TeacherTopicMasteryMatrixResponse | null;
  isLoadingMatrix: boolean;
  matrixError: string | null;
  focusedTopicId: number | null;
  setFocusedTopicId: (topicId: number | null) => void;
  fetchTopicMasteryMatrix: (effectiveTeacherId?: number | null, classId?: number | null) => Promise<void>;
  clearMatrix: () => void;

  resetTeacherStore: () => void;
}

export const useTeacherStore = create<TeacherStoreState>((set) => ({
  classes: [],
  selectedClassId: null,

  selectedStudentId: null,
  setSelectedStudentId: (id) => set({ selectedStudentId: id }),

  adminTeachers: [],
  adminSelectedTeacherId: null,
  isLoadingAdminTeachers: false,

  setClasses: (classes) => set({ classes }),
  setSelectedClass: (id) => set({ selectedClassId: id }),

  setAdminTeachers: (teachers) => set({ adminTeachers: teachers }),
  setAdminSelectedTeacherId: (id) => set({ adminSelectedTeacherId: id }),
  setIsLoadingAdminTeachers: (isLoadingAdminTeachers) => set({ isLoadingAdminTeachers }),

  // Matrix State
  matrixData: null,
  isLoadingMatrix: false,
  matrixError: null,
  focusedTopicId: null,

  setFocusedTopicId: (topicId) => set({ focusedTopicId: topicId }),
  clearMatrix: () => set({ matrixData: null, matrixError: null }),

  fetchTopicMasteryMatrix: async (effectiveTeacherId, classId) => {
    try {
      set({ isLoadingMatrix: true, matrixError: null });
      const httpClient = new HttpClient();
      const params = new URLSearchParams();
      if (effectiveTeacherId) {
        params.append("teacher_id", String(effectiveTeacherId));
      }
      if (classId !== null && classId !== undefined) {
        params.append("class_id", String(classId));
      }
      const qs = params.toString() ? `?${params.toString()}` : "";
      const res = await httpClient.get<TeacherTopicMasteryMatrixResponse>(`/teacher/topics/mastery_matrix${qs}`);
      if (res) {
        set({ matrixData: res, isLoadingMatrix: false });
      }
    } catch (err: any) {
      console.error("Failed to fetch topic mastery matrix:", err);
      set({ matrixError: err?.message || "Failed to fetch matrix", isLoadingMatrix: false });
    }
  },

  resetTeacherStore: () =>
    set({
      classes: [],
      selectedClassId: null,
      selectedStudentId: null,
      adminTeachers: [],
      adminSelectedTeacherId: null,
      isLoadingAdminTeachers: false,
      matrixData: null,
      isLoadingMatrix: false,
      matrixError: null,
      focusedTopicId: null,
    }),
}));

